import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card'
import { Input } from '@shared/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select'
import { Button } from '@shared/components/ui/button'
import { Badge } from '@shared/components/ui/badge'
import { toast } from '@shared/hooks/use-toast'
import { apiUrl } from '@/lib/api'
import SubscriptionTable from './SubscriptionTable'
import SubscriptionDetailsDialog from './SubscriptionDetailsDialog'
import { RefreshCw, Search, Filter, AlertCircle } from 'lucide-react'

interface Subscription {
  id: string
  user_email: string
  business_name: string
  plan_name?: string
  plan_monthly_price_cents?: number
  plan_annual_price_cents?: number
  billing_cycle: string
  status: string
  payment_status: string
  current_period_end?: string
}

interface SubscriptionManagementProps {
  tenantId?: string // If provided, shows subscriptions for a specific tenant
  title?: string
  description?: string
}

export const SubscriptionManagement: React.FC<SubscriptionManagementProps> = ({
  tenantId,
  title = "Subscriptions",
  description = "Manage subscriptions and billing"
}) => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('all')
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null)
  const [showDialog, setShowDialog] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [retryCount, setRetryCount] = useState(0)


  const load = async (isRetry = false) => {
    if (isRetry) {
      setRetryCount(prev => prev + 1)
    }
    
    setLoading(true)
    setError(null)
    
    try {
      // Get fresh token
      const { auth } = await import('@/lib/firebase')
      const token = await auth.currentUser?.getIdToken()
      if (!token) {
        console.error('No authentication token available')
        setError('Authentication required')
        return
      }

      const requestHeaders = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      
      const params = new URLSearchParams()
      if (q) params.set('q', q)
      if (status && status !== 'all') params.set('status', status)
      
      const url = tenantId 
        ? apiUrl(`/api/admin/tenants/${tenantId}/subscriptions?${params.toString()}`)
        : apiUrl(`/api/admin/subscriptions?${params.toString()}`)
      
      const resp = await fetch(url, { headers: requestHeaders })
      
      if (resp.ok) {
        const data = await resp.json()
        setSubscriptions(data.subscriptions || [])
        setRetryCount(0) // Reset retry count on success
      } else {
        const errorData = await resp.json().catch(() => ({}))
        const errorMessage = errorData.error || `Failed to load subscriptions (${resp.status})`
        setError(errorMessage)
        
        if (isRetry) {
          toast({
            title: 'Failed to Load Subscriptions',
            description: errorMessage,
            variant: 'destructive'
          })
        }
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Network error occurred'
      setError(errorMessage)
      
      if (isRetry) {
        toast({
          title: 'Connection Error',
          description: 'Unable to connect to the server. Please check your connection and try again.',
          variant: 'destructive'
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleRetry = () => {
    load(true)
  }

  useEffect(() => { 
    load() 
  }, [q, status, tenantId])

  useEffect(() => {
    (async () => {
      try {
        // Get fresh token
        const { auth } = await import('@/lib/firebase')
        const token = await auth.currentUser?.getIdToken()
        if (!token) {
          console.error('No authentication token available')
          return
        }

        const requestHeaders = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
        const r = await fetch(apiUrl('/api/admin/plans'), { headers: requestHeaders })
        if (r.ok) { 
          const d = await r.json()
          // Plans loaded successfully
        }
      } catch (error) {
        console.error('Failed to load plans:', error)
      }
    })()
  }, [])

  const handleViewSubscription = (subscription: Subscription) => {
    setSelectedSubscription(subscription)
    setShowDialog(true)
  }

  const handleSubscriptionUpdated = () => {
    load() // Refresh the list
    toast({
      title: 'Success',
      description: 'Subscription updated successfully',
    })
  }

  const syncAllSubscriptions = async () => {
    setSyncing(true)
    try {
      // Get fresh token
      const { auth } = await import('@/lib/firebase')
      const token = await auth.currentUser?.getIdToken()
      if (!token) {
        console.error('No authentication token available')
        toast({
          title: 'Authentication Error',
          description: 'Authentication required to sync subscriptions',
          variant: 'destructive'
        })
        return
      }

      const requestHeaders = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      const response = await fetch(apiUrl('/api/admin/subscriptions/sync-all'), {
        method: 'POST',
        headers: requestHeaders
      })
      
      if (response.ok) {
        const data = await response.json()
        toast({
          title: 'Sync Complete',
          description: `Successfully synced ${data.syncedCount || 0} subscriptions with Stripe`,
        })
        load() // Refresh the table
      } else {
        const error = await response.json().catch(() => ({}))
        toast({
          title: 'Sync Failed',
          description: error.error || 'Failed to sync subscriptions with Stripe',
          variant: 'destructive'
        })
      }
    } catch (error) {
      toast({
        title: 'Sync Error',
        description: 'Network error occurred during sync',
        variant: 'destructive'
      })
    } finally {
      setSyncing(false)
    }
  }

  const getStatusCounts = () => {
    const counts = subscriptions.reduce((acc, sub) => {
      acc[sub.status] = (acc[sub.status] || 0) + 1
      return acc
    }, {} as Record<string, number>)
    return counts
  }

  const statusCounts = getStatusCounts()

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                {title}
                {subscriptions.length > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {subscriptions.length} total
                  </Badge>
                )}
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">{description}</p>
            </div>
            
            {/* Status Summary */}
            {subscriptions.length > 0 && (
              <div className="flex gap-2">
                {Object.entries(statusCounts).map(([status, count]) => (
                  <Badge 
                    key={status} 
                    variant={status === 'active' ? 'default' : 'secondary'}
                    className="text-xs"
                  >
                    {status}: {count}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="flex flex-col sm:flex-row gap-2 flex-1">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search email or business" 
                  value={q} 
                  onChange={(e) => setQ(e.target.value)}
                  className="pl-10"
                  aria-label="Search subscriptions"
                />
              </div>
              
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-48">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="canceled">Canceled</SelectItem>
                  <SelectItem value="past_due">Past Due</SelectItem>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2">
              <Button 
                onClick={syncAllSubscriptions}
                disabled={syncing || loading}
                variant="outline"
                className="flex items-center gap-2"
                aria-label="Sync all subscriptions with Stripe"
              >
                <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync All'}
              </Button>
              
              <Button 
                onClick={handleRetry}
                disabled={loading}
                variant="outline"
                className="flex items-center gap-2"
                aria-label="Refresh subscriptions"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
            </div>
          </div>

          {/* Error Banner */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <span className="text-sm text-destructive">{error}</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleRetry}
                className="ml-auto text-destructive hover:text-destructive"
              >
                Retry
              </Button>
            </div>
          )}
        </CardHeader>
        
        <CardContent>
          <SubscriptionTable
            subscriptions={subscriptions}
            onViewSubscription={handleViewSubscription}
            loading={loading}
            searchQuery={q}
            onRetry={handleRetry}
            error={error || undefined}
          />
        </CardContent>
      </Card>

      {selectedSubscription && (
        <SubscriptionDetailsDialog
          subscription={selectedSubscription}
          isOpen={showDialog}
          onClose={() => {
            setShowDialog(false)
            setSelectedSubscription(null)
          }}
          onSubscriptionUpdated={handleSubscriptionUpdated}
        />
      )}
    </div>
  )
}